import {
  Box,
  Divider,
  HStack,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  Button,
} from '@chakra-ui/react'
import type {
  DocumentationVersionPageDetail,
  DocumentationVersionPageSection,
  DocumentationVersionPageSummary,
} from '@/services/admin/documentationService'
import { useTranslation } from 'react-i18next'

interface DocumentationContentPagesTabProps {
  isLoading: boolean
  sections: DocumentationVersionPageSection[]
  selectedPageSlug: string
  pageDetailLoading: boolean
  pageDetail?: DocumentationVersionPageDetail
  onSelectPageSlug: (value: string) => void
  formatDateTime: (value?: string | null) => string
}

export function DocumentationContentPagesTab({
  isLoading,
  sections,
  selectedPageSlug,
  pageDetailLoading,
  pageDetail,
  onSelectPageSlug,
  formatDateTime,
}: DocumentationContentPagesTabProps) {
  const { t } = useTranslation()

  const renderPageListItem = (page: DocumentationVersionPageSummary) => (
    <Button
      key={page.slug}
      variant={selectedPageSlug === page.slug ? 'solid' : 'ghost'}
      justifyContent="flex-start"
      onClick={() => onSelectPageSlug(page.slug)}
      size="sm"
      width="100%"
      h="auto"
      minH={8}
      py={2}
      whiteSpace="normal"
      textAlign="left"
      lineHeight="short"
      overflowWrap="anywhere"
    >
      {page.title}
    </Button>
  )

  if (isLoading) {
    return (
      <HStack py={8} justify="center">
        <Spinner />
        <Text>{t('documentationContent.pages.loadingPages')}</Text>
      </HStack>
    )
  }

  return (
    <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
      <Box borderWidth="1px" borderRadius="md" p={3}>
        <Text fontWeight="semibold" mb={3}>
          {t('documentationContent.pages.pageTree')}
        </Text>
        {sections.length === 0 ? (
          <Text color="gray.500">{t('documentationContent.pages.noPages')}</Text>
        ) : (
          <VStack align="stretch" spacing={3}>
            {sections.map((section) => (
              <Box key={section.section}>
                <Text fontSize="sm" color="gray.500" mb={2}>{section.section}</Text>
                <VStack align="stretch" spacing={1}>
                  {section.pages.map((page) => renderPageListItem(page))}
                </VStack>
              </Box>
            ))}
          </VStack>
        )}
      </Box>

      <Box borderWidth="1px" borderRadius="md" p={3}>
        <Text fontWeight="semibold" mb={3}>
          {t('documentationContent.pages.pageDetail')}
        </Text>
        {!selectedPageSlug ? (
          <Text color="gray.500">{t('documentationContent.pages.selectPage')}</Text>
        ) : pageDetailLoading ? (
          <HStack py={6} justify="center">
            <Spinner />
            <Text>{t('documentationContent.pages.loadingPageDetail')}</Text>
          </HStack>
        ) : pageDetail ? (
          <VStack align="stretch" spacing={3}>
            <Box>
              <Text fontWeight="semibold">{pageDetail.title}</Text>
              <Text fontSize="sm" color="gray.500">{pageDetail.slug}</Text>
            </Box>
            <Text fontSize="sm" color="gray.600">
              {pageDetail.description || t('documentationContent.pages.noDescription')}
            </Text>
            <Divider />
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
              <Text fontSize="xs" color="gray.500">
                {t('documentationContent.pages.nav')}: {pageDetail.navSection} / {pageDetail.navOrder}
              </Text>
              <Text fontSize="xs" color="gray.500">
                {t('documentationContent.pages.updated')}: {formatDateTime(pageDetail.updatedAt)}
              </Text>
              <Text fontSize="xs" color="gray.500">
                {t('documentationContent.pages.effective')}: {formatDateTime(pageDetail.effectiveDate)}
              </Text>
              <Text fontSize="xs" color="gray.500">
                {t('documentationContent.pages.lastUpdated')}: {formatDateTime(pageDetail.lastUpdatedAt)}
              </Text>
            </SimpleGrid>
            <Text fontSize="xs" color="gray.500" overflowWrap="anywhere">
              {t('documentationContent.pages.source')}: {pageDetail.sourcePath}
            </Text>
            <Text fontSize="xs" color="gray.500" overflowWrap="anywhere">
              {t('documentationContent.pages.sha')}: {pageDetail.sourceSha}
            </Text>
            <Box borderWidth="1px" borderRadius="md" p={3} maxH="360px" overflowY="auto">
              <Box fontSize="sm" dangerouslySetInnerHTML={{ __html: pageDetail.html }} />
            </Box>
          </VStack>
        ) : (
          <Text color="red.500">{t('documentationContent.pages.loadPageDetailFailed')}</Text>
        )}
      </Box>
    </SimpleGrid>
  )
}
